import { defineComponent as _defineComponent } from 'vue'
import router from '@/router';
import { BILLING_PLAN_DETAILS } from '@/routerNames';
import billingService from '@/services/BillingService';
import { computed, onMounted, ref, watch } from 'vue';
import BackSnaploreButton from '@/components/pages/billing/PlansPage/BackSnaploreButton.vue';
import store from '@/store';
import { Price, Subscription } from '@/@types/billing';
import { SubscriptionType } from '@/@types/enums/SubscriptionType';
import PlansEnterpriseFooter from '@/components/pages/billing/PlansPage/PlansEnterpriseFooter.vue';
import PricingTabs from '@/components/pages/billing/PlansPage/PricingTabs/PricingTabs.vue';
import apiService from '@/services/ApiService';
import { Loading } from 'element-ui';
import { useRoute } from 'vue-router/composables';
import moment from 'moment';
import { GUIDES_APP, SNAPLORE_APP, isGuidesContext, isGuidesOnlyCompany } from '@/helpers/appContext';
import { BILLING_ERROR_MESSAGES, billingErrorMessage, isPaymentPendingError } from '@/helpers/billingErrors';
import { findCompanySubscription, priceListStatus, resolvePlansPageIsGuides } from '@/helpers/planProduct';
import notificationService from '@/services/NotificationService';


export default /*#__PURE__*/_defineComponent({
  __name: 'PlansPage',
  setup(__props) {

const route = useRoute();

const prices = ref<Price[]>([]);
const subscription = ref<Subscription>();

const requiresUpdate = computed(() => !!route.params.update);
const company = computed(() => store.state.company);
const isCompanyPaid = computed(() => store.state.company.isPaid);
const isUpdatePlan = computed(() => requiresUpdate.value && isCompanyPaid.value);

// C7: a change-plan visit must derive the product from the company's ACTUAL
// current subscription tag, not the session/query context — a fresh tab
// (empty sessionStorage) opening Settings → Billing → Change Plan otherwise
// showed Snaplore prices for a Guides-only company. Guides signups reach this
// page via the paid-company middleware (query params are dropped there) — the
// context is carried by sessionStorage since the auth pages for the initial
// subscribe flow, with the route query as an explicit override.
const isGuides = computed(() =>
  resolvePlansPageIsGuides({
    isUpdatePlan: isUpdatePlan.value,
    companyPaddleApp: company.value?.paddle?.app,
    queryIsGuides: isGuidesContext(route.query),
    queryIsSnaplore: route.query.app === SNAPLORE_APP,
    companyIsGuidesOnly: isGuidesOnlyCompany(company.value),
  })
);

// One product is past due while the other keeps the company paid: a second subscription is refused, so the way out is
// the card (SNAP-2241). A company with nothing else paid goes through the not-paid page instead.
const productWithFailedPayment = computed(() => {
  if (isUpdatePlan.value) return null;
  if (isGuides.value) {
    return company.value?.guidesSubscription?.status === SubscriptionType.PAST_DUE ? 'Guides' : null;
  }
  const isSnaplorePastDue =
    route.query.app === SNAPLORE_APP &&
    company.value?.paddle?.status === SubscriptionType.PAST_DUE &&
    company.value?.paddle?.app !== GUIDES_APP;
  return isSnaplorePastDue && isCompanyPaid.value ? 'Snaplore' : null;
});

const companyStatusForPrices = computed(() =>
  priceListStatus({
    isUpdatePlan: isUpdatePlan.value,
    isGuides: isGuides.value,
    paddleStatus: company.value?.paddle?.status,
    paddleApp: company.value?.paddle?.app,
    guidesStatus: company.value?.guidesSubscription?.status,
  })
);

// The backend answered PAYMENT_PENDING: a checkout was paid and only its webhook is missing.
const paymentPending = ref(false);
const paymentPendingMessage = BILLING_ERROR_MESSAGES.PAYMENT_PENDING;

const handlePlanUpdate = async (priceId: Price['id']) => {
  // Check if there's a scheduled change within 35 minutes
  if (subscription.value?.currentBillingPeriod?.endsAt) {
    const nextBilledAt = moment.utc(subscription.value?.currentBillingPeriod?.endsAt);
    const now = moment.utc();
    const minutesUntilChange = nextBilledAt.diff(now, 'minutes');

    if (minutesUntilChange <= 35 && minutesUntilChange > 0) {
      apiService.handleResponseError({
        response: {
          status: 400,
          data: {
            error:
              'Plan changes are temporarily unavailable. Your subscription update is being processed and will be effective soon.',
          },
        },
      });
      return;
    }
  }

  const loader = Loading.service({ lock: true });

  const subscriptionId = company.value.paddle.subscriptionId;

  try {
    await apiService.billing.changePlan({
      subscriptionId,
      priceId,
    });
    await router.push({ name: BILLING_PLAN_DETAILS });
  } catch (e) {
    const readableMessage = billingErrorMessage(e);
    if (readableMessage) {
      notificationService.error(readableMessage);
    } else {
      apiService.handleResponseError(e);
    }
  }

  loader.close();
  loader.$destroy();
};

const handleSubscribe = async (priceId: Price['id']) => {
  try {
    const {
      data: { transactionId },
    } = await apiService.billing.createTransaction({ priceId, ...(isGuides.value ? { product: GUIDES_APP } : {}) });
    const checkoutApp = isGuides.value ? GUIDES_APP : route.query.app === SNAPLORE_APP ? SNAPLORE_APP : undefined;
    billingService.redirectToCheckout(transactionId, checkoutApp ? { app: checkoutApp } : undefined);
  } catch (error) {
    if (isPaymentPendingError(error)) paymentPending.value = true;
    const readableMessage = billingErrorMessage(error);
    if (readableMessage) {
      notificationService.error(readableMessage);
    } else {
      apiService.handleResponseError(error);
    }
  }
};

const loadPrices = async () => {
  const { data } = await apiService.billing.getPrices(isGuides.value ? GUIDES_APP : undefined);
  prices.value = data.sort((a: any, b: any) => {
    // Prioritize objects with a trialPeriod
    if (a.trialPeriod && !b.trialPeriod) return -1;
    if (!a.trialPeriod && b.trialPeriod) return 1;
    return a - b;
  });
};

onMounted(async () => {
  const loader = Loading.service({ lock: true });

  // Only a plan change needs the current subscription, and elsewhere its interval would hide prices. A non-billing
  // company counts as paid with no subscription, and its owner usually has no Paddle customer, so this would answer
  // 404. Failing or not, it must not keep the prices from loading.
  try {
    if (isUpdatePlan.value && company.value?.paddle?.subscriptionId) {
      await apiService.billing.getUserSubscriptions().then(({ data }) => {
        subscription.value = findCompanySubscription(data, company.value);
      });
    }
  } catch (e) {
    apiService.handleResponseError(e);
  }

  try {
    await loadPrices();
  } catch (e) {
    apiService.handleResponseError(e);
  }

  loader.close();
  loader.$destroy();
});

// The product selector can change `?app=` on this very page, which reuses the component.
watch(isGuides, () => loadPrices().catch((e) => apiService.handleResponseError(e)));

return { __sfc: true,route, prices, subscription, requiresUpdate, company, isCompanyPaid, isUpdatePlan, isGuides, productWithFailedPayment, companyStatusForPrices, paymentPending, paymentPendingMessage, handlePlanUpdate, handleSubscribe, loadPrices, BILLING_PLAN_DETAILS, BackSnaploreButton, PlansEnterpriseFooter, PricingTabs }
}

})